'use client';

import React, { useState } from 'react';
import { ChevronRight, Info } from 'lucide-react';

interface StepBudgetProps {
  initialBudget?: number;
  initialCurrency?: string;
  onNext: (budget: number, currency: string) => void;
}

const CURRENCIES = [
  { code: 'USD', symbol: '$', label: 'US Dollar' },
  { code: 'EUR', symbol: '€', label: 'Euro' },
  { code: 'GBP', symbol: '£', label: 'British Pound' },
  { code: 'CAD', symbol: 'C$', label: 'Canadian Dollar' },
  { code: 'AUD', symbol: 'A$', label: 'Australian Dollar' },
  { code: 'SGD', symbol: 'S$', label: 'Singapore Dollar' },
];

const BUDGET_PRESETS = [150, 300, 500, 800, 1200, 2000];

const getBudgetTier = (budget: number): { label: string; description: string; color: string } => {
  if (budget < 200) return { label: 'High Street', description: 'Zara, H&M, ASOS, Mango', color: '#7BA05B' };
  if (budget < 500) return { label: 'Contemporary', description: 'COS, & Other Stories, Reiss', color: '#5B8FA0' };
  if (budget < 1000) return { label: 'Premium', description: 'AllSaints, Ted Baker, Sandro', color: '#C9A96E' };
  if (budget < 2000) return { label: 'Designer', description: 'Acne Studios, A.P.C., Isabel Marant', color: '#9B6E8A' };
  return { label: 'Luxury', description: 'Gucci, Bottega Veneta, Prada', color: '#1A1A1A' };
};

export default function StepBudget({ initialBudget, initialCurrency, onNext }: StepBudgetProps) {
  const [budget, setBudget] = useState<string>(initialBudget ? String(initialBudget) : '');
  const [currency, setCurrency] = useState(initialCurrency ?? 'USD');
  const [focused, setFocused] = useState(false);

  const numericBudget = parseFloat(budget);
  const isValid = !isNaN(numericBudget) && numericBudget >= 50;
  const tier = isValid ? getBudgetTier(numericBudget) : null;
  const selectedCurrency = CURRENCIES.find(c => c.code === currency) ?? CURRENCIES[0];

  const handleNext = () => {
    if (!isValid) return;
    onNext(numericBudget, currency);
  };

  const handlePreset = (val: number) => {
    setBudget(String(val));
  };

  return (
    <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-10 2xl:px-16 py-6 sm:py-10 lg:py-16">
      <div className="mb-6 sm:mb-10 animate-fade-in-up">
        <p className="text-xs font-semibold uppercase tracking-widest mb-2 sm:mb-3" style={{ color: '#C9A96E', letterSpacing: '0.12em' }}>
          Step 2 of 4
        </p>
        <h1 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl xl:text-6xl mb-2 sm:mb-3" style={{ color: '#1A1A1A' }}>
          What&apos;s your budget?
        </h1>
        <p className="text-sm sm:text-base lg:text-lg" style={{ color: 'hsl(30 8% 52%)' }}>
          We&apos;ll build an outfit that makes every dollar count.
        </p>
      </div>

      <div className="max-w-lg">
        {/* Currency selector */}
        <div className="mb-5 sm:mb-6">
          <label className="block text-sm font-medium mb-2" style={{ color: '#1A1A1A' }}>
            Currency
          </label>
          <div className="flex flex-wrap gap-1.5 sm:gap-2">
            {CURRENCIES.map(c => (
              <button
                key={`currency-${c.code}`}
                onClick={() => setCurrency(c.code)}
                className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium border-2 transition-all duration-150 active:scale-95 hover:scale-105 hover:-translate-y-px`}
                style={{
                  borderColor: currency === c.code ? '#C9A96E' : 'hsl(35 20% 88%)',
                  backgroundColor: currency === c.code ? 'rgba(201,169,110,0.08)' : 'white',
                  color: currency === c.code ? '#1A1A1A' : 'hsl(30 8% 52%)',
                  boxShadow: currency === c.code ? '0 4px 12px rgba(201,169,110,0.2)' : '0 1px 3px rgba(0,0,0,0.06)',
                }}
              >
                {c.symbol} {c.code}
              </button>
            ))}
          </div>
        </div>

        {/* Budget input */}
        <div className="mb-5 sm:mb-6">
          <label className="block text-sm font-medium mb-2" style={{ color: '#1A1A1A' }}>
            Total outfit budget
          </label>
          <p className="text-xs mb-2 sm:mb-3" style={{ color: 'hsl(30 8% 52%)' }}>
            This covers all items combined — head to toe.
          </p>
          <div
            className="flex items-center rounded-2xl border-2 overflow-hidden transition-all duration-200 hover:shadow-md"
            style={{
              borderColor: focused ? '#C9A96E' : 'hsl(35 20% 88%)',
              boxShadow: focused ? '0 0 0 4px rgba(201,169,110,0.12), 0 4px 16px rgba(201,169,110,0.1)' : '0 1px 4px rgba(0,0,0,0.06)',
              backgroundColor: 'white',
            }}
          >
            <span
              className="px-3 sm:px-4 py-3 sm:py-4 text-lg sm:text-xl font-semibold border-r"
              style={{ color: '#C9A96E', borderColor: 'hsl(35 20% 88%)' }}
            >
              {selectedCurrency.symbol}
            </span>
            <input
              type="number"
              value={budget}
              onChange={(e) => setBudget(e.target.value)}
              onFocus={() => setFocused(true)}
              onBlur={() => setFocused(false)}
              placeholder="350"
              min={50}
              max={50000}
              className="flex-1 px-3 sm:px-4 py-3 sm:py-4 text-xl sm:text-2xl font-semibold outline-none bg-transparent tabular-nums"
              style={{ color: '#1A1A1A', fontFamily: 'Plus Jakarta Sans, sans-serif' }}
            />
            <span className="px-3 sm:px-4 text-xs sm:text-sm font-medium" style={{ color: 'hsl(30 8% 52%)' }}>
              {currency}
            </span>
          </div>
          {budget && !isValid && (
            <p className="text-xs mt-2" style={{ color: '#E05252' }}>
              Minimum budget is {selectedCurrency.symbol}50
            </p>
          )}
        </div>

        {/* Quick presets */}
        <div className="mb-6 sm:mb-8">
          <label className="block text-xs font-medium mb-2 sm:mb-3 uppercase tracking-widest" style={{ color: 'hsl(30 8% 52%)', letterSpacing: '0.1em' }}>
            Quick select
          </label>
          <div className="flex flex-wrap gap-1.5 sm:gap-2">
            {BUDGET_PRESETS.map(preset => (
              <button
                key={`preset-${preset}`}
                onClick={() => handlePreset(preset)}
                className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium border transition-all duration-150 active:scale-95 hover:scale-105 hover:-translate-y-px`}
                style={{
                  borderColor: budget === String(preset) ? '#C9A96E' : 'hsl(35 20% 88%)',
                  backgroundColor: budget === String(preset) ? 'rgba(201,169,110,0.08)' : 'white',
                  color: budget === String(preset) ? '#1A1A1A' : 'hsl(30 8% 52%)',
                  boxShadow: budget === String(preset) ? '0 4px 12px rgba(201,169,110,0.2)' : '0 1px 3px rgba(0,0,0,0.06)',
                }}
              >
                {selectedCurrency.symbol}{preset.toLocaleString()}
              </button>
            ))}
          </div>
        </div>

        {/* Budget tier indicator */}
        {tier && (
          <div
            className="flex items-start gap-2.5 sm:gap-3 p-3 sm:p-4 rounded-xl mb-6 sm:mb-8 animate-fade-in"
            style={{ backgroundColor: `${tier.color}12`, border: `1px solid ${tier.color}30` }}
          >
            <Info size={15} className="mt-0.5 shrink-0" style={{ color: tier.color }} />
            <div>
              <span className="text-sm font-semibold" style={{ color: tier.color }}>
                {tier.label} tier
              </span>
              <p className="text-xs mt-0.5" style={{ color: 'hsl(30 8% 52%)' }}>
                We&apos;ll pull from brands like {tier.description}
              </p>
            </div>
          </div>
        )}

        <button
          onClick={handleNext}
          disabled={!isValid}
          className={`
            inline-flex items-center gap-2 sm:gap-2.5 px-6 sm:px-8 py-3 sm:py-3.5 rounded-full text-sm font-semibold
            transition-all duration-150 active:scale-95
            ${isValid ? 'hover:opacity-90 hover:shadow-lg' : 'opacity-40 cursor-not-allowed'}
          `}
          style={{ backgroundColor: isValid ? '#1A1A1A' : '#8A8078', color: 'white' }}
        >
          Continue
          <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
}